/**
 * Is this donation row something a creator should actually be shown?
 *
 * Found 2026-09-24: the checkout handler wrote a `pending` donation row BEFORE
 * Stripe was called, so anything that reached it -- an abandoned form, a test
 * script, a probe -- appeared in a creator's own donation history as if somebody
 * had given. One such row sat on the only athlete who has ever been paid, for
 * her to read.
 *
 * The write now happens after Stripe accepts the session, which stops new
 * phantoms. This is the second layer, and the one that covers the rows already
 * in the database and every genuinely abandoned checkout: a row counts once
 * Stripe has said something about it.
 *
 * These three statuses are exactly the set MissionPage already uses to decide
 * that somebody is a supporter, so the creator's list and the supporter check
 * cannot disagree.
 *
 * Takes `unknown` on purpose -- every caller has a raw Firestore document.
 */
const SETTLED = new Set(['succeeded', 'active', 'processing']);

export function isRealDonation(d: unknown): boolean {
  const status = (d as { status?: unknown } | null | undefined)?.status;
  return SETTLED.has(String(status ?? 'pending'));
}

/**
 * What the creator actually receives from this row.
 *
 * Set by Jussi 2026-09-24: a creator's dashboard shows THEIR money, not the
 * gross the donor was charged. On the portfolio's first real donation the donor
 * paid $53.50, our platform fee was $3.50 and the athlete receives $50.00 --
 * her dashboard must say $50.00. Our fee is our business.
 *
 * `amount` is already the net figure everywhere it is written (the checkout
 * handler writes the base amount, and both webhook paths write
 * gross - application_fee). This exists so that every creator-facing surface
 * routes through one named function that CANNOT return `grossAmount`, and so
 * the guard in functions/test/creatorSeesNet.test.cjs has something to anchor
 * on. Internal reporting still needs the gross -- it reads Stripe directly, via
 * the Control Center's revenue / net_revenue sync, never this.
 */
export function creatorAmount(d: unknown): number {
    const n = Number((d as { amount?: unknown } | null | undefined)?.amount ?? 0);
    return Number.isFinite(n) ? n : 0;
}
